// chat.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ChatState,
  ChatMessage,
  GENERATION_MILESTONES,
} from "./chat-interfaces";
import {
  addAgent,
  createAgent,
  generateApplication,
  importApplication,
} from "./chat-async-calls";

const initialState: ChatState = {
  messages: [],
  loading: false,
  generationProgress: 0,
  currentStep: "",
  currentRequestId: null,
  error: null,
  currentMilestone: 0,
  totalMilestones: GENERATION_MILESTONES.length,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addMessage: (
      state,
      action: PayloadAction<Omit<ChatMessage, "timestamp" | "id">>
    ) => {
      state.messages.push({
        ...action.payload,
        // --- FIX: Generate a truly unique ID to prevent key collisions ---
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });
    },
    updateLastMessage: (state, action: PayloadAction<Partial<ChatMessage>>) => {
      const lastIndex = state.messages.length - 1;
      if (lastIndex >= 0) {
        state.messages[lastIndex] = {
          ...state.messages[lastIndex],
          ...action.payload,
        };
      }
    },
    // ... rest of the slice remains the same
    updateMilestoneProgress: (
      state,
      action: PayloadAction<{
        message: string;
        step?: number;
        progress?: number;
      }>
    ) => {
      const { message, step, progress } = action.payload;

      // Find matching milestone
      const milestone = GENERATION_MILESTONES.find(
        (m) =>
          message.toLowerCase().includes(m.message.toLowerCase()) ||
          message.toLowerCase().includes(m.agent.toLowerCase())
      );

      if (milestone) {
        state.currentMilestone = milestone.step;
        state.generationProgress = milestone.progress;
        state.currentStep = `${milestone.agent}: ${milestone.message}`;

        // Find and update the loading message (progress bar message)
        const loadingMessageIndex = state.messages.findIndex(
          (msg) => msg.isLoading
        );
        if (loadingMessageIndex >= 0) {
          state.messages[loadingMessageIndex] = {
            ...state.messages[loadingMessageIndex],
            progress: milestone.progress,
            currentStep: state.currentStep,
            milestoneStep: milestone.step,
            text: `🚀 Generating application...`,
          };
        }
      } else if (step !== undefined && progress !== undefined) {
        // Manual progress update
        state.currentMilestone = step;
        state.generationProgress = progress;
        state.currentStep = message;

        // Update loading message
        const loadingMessageIndex = state.messages.findIndex(
          (msg) => msg.isLoading
        );
        if (loadingMessageIndex >= 0) {
          state.messages[loadingMessageIndex] = {
            ...state.messages[loadingMessageIndex],
            progress: progress,
            currentStep: message,
            milestoneStep: step,
          };
        }
      }
    },
    resetProgress: (state) => {
      state.generationProgress = 0;
      state.currentStep = "";
      state.currentMilestone = 0;
    },
    clearChat: (state) => {
      state.messages = [];
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
      state.generationProgress = 0;
      state.currentStep = "";
      state.currentMilestone = 0;
    },
    resetError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (!action.payload) {
        // Reset progress when loading stops
        state.generationProgress = 0;
        state.currentStep = "";
        state.currentMilestone = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateApplication.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.arg.requestId;
        state.generationProgress = 0;
        state.currentMilestone = 0;
      })
      .addCase(generateApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.generationProgress = 0;
        state.currentMilestone = 0;
      })
      .addCase(generateApplication.fulfilled, (state, action) => {
        // We set loading to false in the component after import,
        // so we don't set it here to keep the progress bar visible.
      })
      .addCase(importApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addMessage,
  updateLastMessage,
  updateMilestoneProgress,
  resetProgress,
  clearChat,
  resetError,
  setLoading,
} = chatSlice.actions;

export default chatSlice.reducer;
